import { ImageWithFallback } from "../figma/ImageWithFallback";
import { Button } from "../ui/button";
import { EnrollmentModal } from "../EnrollmentModal";
import { useState } from 'react';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';

const categories = ["All", "Learning", "Arts & Crafts", "Outdoor", "Science", "Music", "Special Events"];

const galleryImages = [
  {
    src: "styles/learning.jpg",
    category: "Learning",
    title: "Interactive Learning Session"
  },
  {
    src: "styles/gallery_play_area.jpg",
    alt: "Kids learning together in classroom",
    category: "Learning",
    title: "Collaborative Learning"
  },
  {
    src: "https://images.unsplash.com/photo-1611085667203-7efa7c067bce?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxjaGlsZHJlbiUyMGFydCUyMGNyZWF0aXZlfGVufDF8fHx8MTc1NjkyNTg0MXww&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral",
    alt: "Children doing art and crafts",
    category: "Arts & Crafts",
    title: "Creative Arts Workshop"
  },
  {
    src: "styles/outdoor.jpg",
    alt: "Kids playing outdoors",
    category: "Outdoor",
    title: "Outdoor Adventure Time"
  },
  {
    src: "styles/games_activity.jpg",
    alt: "Science exploration with kids",
    category: "Science",
    title: "Science Discovery Lab"
  },
  {
    src: "styles/arts.jpg",
    alt: "Child reading book",
    category: "Learning",
    title: "Reading Adventures"
  },
  {
    src: "https://images.unsplash.com/photo-1631788354545-233eb4de176f?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxjaGlsZHJlbiUyMHBsYXlpbmclMjBlZHVjYXRpb25hbCUyMGdhbWVzfGVufDF8fHx8MTc1NjkyNTg5MXww&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral",
    alt: "Children playing educational games",
    category: "Learning",
    title: "Educational Game Time"
  },
  {
    src: "https://images.unsplash.com/photo-1542666836-b0b37e7e22d3?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxraWRzJTIwc2NpZW5jZSUyMGV4cGVyaW1lbnR8ZW58MXx8fHwxNzU2ODE3ODg0fDA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral",
    alt: "Kids doing science experiment",
    category: "Science",
    title: "Hands-on Experiments"
  },
  {
    src: "https://images.unsplash.com/photo-1586503452950-997923af27f9?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxoYXBweSUyMGNoaWxkJTIwbGVhcm5pbmclMjBlZHVjYXRpb258ZW58MXx8fHwxNzU2OTI1ODM5fDA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral",
    alt: "Happy child learning",
    category: "Special Events",
    title: "Celebration Day"
  }
];

export function GalleryPage() {
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [selectedImage, setSelectedImage] = useState<number | null>(null);
  const [isEnrollmentModalOpen, setIsEnrollmentModalOpen] = useState(false);

  const filteredImages = selectedCategory === "All" 
    ? galleryImages 
    : galleryImages.filter(img => img.category === selectedCategory);

  const openLightbox = (index: number) => {
    setSelectedImage(index);
  };

  const closeLightbox = () => {
    setSelectedImage(null);
  };

  const goToNext = () => {
    if (selectedImage !== null) {
      setSelectedImage((selectedImage + 1) % filteredImages.length);
    }
  };

  const goToPrevious = () => {
    if (selectedImage !== null) {
      setSelectedImage((selectedImage - 1 + filteredImages.length) % filteredImages.length);
    }
  };

  return (
    <div>
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 to-purple-50 py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <span className="bg-green-100 text-green-600 px-4 py-2 rounded-full text-sm font-medium">
              Photo Gallery
            </span>
            <h1 className="text-5xl font-bold text-gray-800 mt-4 mb-6">
              Moments of Joy & Discovery
            </h1>
            <p className="text-lg text-gray-600 leading-relaxed">
              Take a glimpse into our vibrant learning environment where children explore, 
              create, and grow together every day. These precious moments capture the essence of childhood learning.
            </p>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 bg-white border-b">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap justify-center gap-2">
            {categories.map((category) => (
              <Button
                key={category}
                variant={selectedCategory === category ? "default" : "outline"}
                className={`px-6 py-2 rounded-full ${
                  selectedCategory === category
                    ? "bg-orange-400 hover:bg-orange-500 text-white"
                    : "text-gray-600 hover:text-orange-400"
                }`}
                onClick={() => setSelectedCategory(category)}
              >
                {category}
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {filteredImages.map((image, index) => (
              <div 
                key={index}
                className="relative group cursor-pointer overflow-hidden rounded-lg shadow-md hover:shadow-xl transition-all duration-300"
                onClick={() => openLightbox(index)}
              >
                <ImageWithFallback
                  src={image.src}
                  alt={image.alt}
                  className="w-full h-64 object-cover transition-transform duration-300 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="absolute bottom-4 left-4 right-4">
                    <h3 className="text-white font-medium text-sm mb-1">{image.title}</h3>
                    <span className="bg-orange-400 text-white px-2 py-1 rounded-full text-xs">
                      {image.category}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {filteredImages.length === 0 && (
            <div className="text-center py-20">
              <p className="text-gray-500 text-lg">No images found for this category.</p>
            </div>
          )}
        </div>
      </section>

      {/* Lightbox */}
      {selectedImage !== null && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4">
          <div className="relative max-w-4xl max-h-full">
            <Button
              variant="ghost"
              size="icon"
              className="absolute -top-12 right-0 text-white hover:bg-white/10 z-10"
              onClick={closeLightbox}
            >
              <X className="w-6 h-6" />
            </Button>
            
            <Button
              variant="ghost"
              size="icon"
              className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:bg-white/10 z-10"
              onClick={goToPrevious}
            >
              <ChevronLeft className="w-8 h-8" />
            </Button>
            
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:bg-white/10 z-10"
              onClick={goToNext}
            >
              <ChevronRight className="w-8 h-8" />
            </Button>
            
            <ImageWithFallback
              src={filteredImages[selectedImage].src}
              alt={filteredImages[selectedImage].alt}
              className="max-w-full max-h-full object-contain rounded-lg"
            />
            
            <div className="absolute bottom-4 left-4 right-4 text-center">
              <h3 className="text-white text-lg font-medium mb-2">
                {filteredImages[selectedImage].title}
              </h3>
              <span className="bg-orange-400 text-white px-3 py-1 rounded-full text-sm">
                {filteredImages[selectedImage].category}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Call to Action */}
      <section className="py-20 bg-gradient-to-r from-orange-400 via-pink-400 to-purple-500">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Want to See Your Child Here?
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Join our community and let your child be part of these wonderful learning experiences.
          </p>
          <Button 
            className="bg-white text-orange-500 hover:bg-gray-100 px-8 py-3 rounded-full text-lg font-medium"
            onClick={() => setIsEnrollmentModalOpen(true)}
          >
            Schedule a Visit Today
          </Button>
        </div>
      </section>
      
      <EnrollmentModal 
        isOpen={isEnrollmentModalOpen} 
        onClose={() => setIsEnrollmentModalOpen(false)} 
      />
    </div>
  );
}